import { getAnalyticsShopId } from '../onboarding/analyticsSession'

const SESSION_STORAGE_KEY = 'invoice-virtual-tour-analytics-session'
const SESSION_TTL_MS = 24 * 60 * 60 * 1000

const VIRTUAL_TOUR_MILESTONES = {
  started: 'started',
  completed: 'completed',
  dismissed: 'dismissed',
  abandoned: 'abandoned',
} as const

export type VirtualTourAnalyticsMilestone =
  | (typeof VIRTUAL_TOUR_MILESTONES)[keyof typeof VIRTUAL_TOUR_MILESTONES]
  | `step_viewed:${number}`

const VIRTUAL_TOUR_TIMESTAMPS = {
  started: 'started',
  completed: 'completed',
  dismissed: 'dismissed',
  abandoned: 'abandoned',
} as const

export type VirtualTourAnalyticsTimestampKey =
  (typeof VIRTUAL_TOUR_TIMESTAMPS)[keyof typeof VIRTUAL_TOUR_TIMESTAMPS]

export type VirtualTourAnalyticsSession = {
  sessionId: string
  shopId: string
  startedAt: string
  ended: boolean
  tourSlug: string
  tourLabel: string
  tourStepCount: number
  launchId: string
  contentfulEnabled: boolean
  themeEditCodeFlag: boolean
  hasThemeId: boolean
  milestones: Partial<Record<VirtualTourAnalyticsMilestone, true>>
  timestamps: Partial<Record<VirtualTourAnalyticsTimestampKey, string>>
  stepTimestamps: Record<number, string>
}

const createEmptySession = (
  sessionId: string,
  shopId: string,
  context: {
    tourSlug: string
    tourLabel: string
    tourStepCount: number
    launchId: string
    contentfulEnabled: boolean
    themeEditCodeFlag: boolean
    hasThemeId: boolean
  },
): VirtualTourAnalyticsSession => ({
  sessionId,
  shopId,
  startedAt: new Date().toISOString(),
  ended: false,
  tourSlug: context.tourSlug,
  tourLabel: context.tourLabel,
  tourStepCount: context.tourStepCount,
  launchId: context.launchId,
  contentfulEnabled: context.contentfulEnabled,
  themeEditCodeFlag: context.themeEditCodeFlag,
  hasThemeId: context.hasThemeId,
  milestones: {},
  timestamps: {},
  stepTimestamps: {},
})

const safeParse = (value: string | null): VirtualTourAnalyticsSession | null => {
  if (!value) return null
  try {
    return JSON.parse(value) as VirtualTourAnalyticsSession
  } catch {
    return null
  }
}

const readStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  return window.sessionStorage.getItem(key)
}

const writeStorageItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(key, value)
}

const removeStorageItem = (key: string) => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(key)
}

const isSessionExpired = (session: VirtualTourAnalyticsSession): boolean => {
  const started = Date.parse(session.startedAt)
  if (Number.isNaN(started)) return true
  return Date.now() - started > SESSION_TTL_MS
}

const isSessionForCurrentShop = (session: VirtualTourAnalyticsSession): boolean => {
  const shopId = getAnalyticsShopId()
  if (!shopId) return true
  if (!session.shopId) return false
  return session.shopId === shopId
}

export const readVirtualTourAnalyticsSession = (): VirtualTourAnalyticsSession | null => {
  const session = safeParse(readStorageItem(SESSION_STORAGE_KEY))
  if (!session || session.ended || isSessionExpired(session)) return null
  if (!isSessionForCurrentShop(session)) {
    removeStorageItem(SESSION_STORAGE_KEY)
    return null
  }
  return session
}

const writeVirtualTourAnalyticsSession = (session: VirtualTourAnalyticsSession) => {
  writeStorageItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

export const ensureVirtualTourAnalyticsSession = (
  context: {
    tourSlug: string
    tourLabel: string
    tourStepCount: number
    launchId: string
    contentfulEnabled: boolean
    themeEditCodeFlag: boolean
    hasThemeId: boolean
  },
): VirtualTourAnalyticsSession => {
  endVirtualTourAnalyticsSession()

  const session = createEmptySession(crypto.randomUUID(), getAnalyticsShopId(), context)
  writeVirtualTourAnalyticsSession(session)
  return session
}

export const endVirtualTourAnalyticsSession = () => {
  removeStorageItem(SESSION_STORAGE_KEY)
}

export const hasVirtualTourMilestoneFired = (
  milestone: VirtualTourAnalyticsMilestone,
): boolean => {
  const session = readVirtualTourAnalyticsSession()
  return Boolean(session?.milestones[milestone])
}

export const markVirtualTourMilestoneFired = (
  milestone: VirtualTourAnalyticsMilestone,
): boolean => {
  const session = readVirtualTourAnalyticsSession()
  if (!session || session.milestones[milestone]) return false

  const next: VirtualTourAnalyticsSession = {
    ...session,
    milestones: { ...session.milestones, [milestone]: true },
  }
  writeVirtualTourAnalyticsSession(next)
  return true
}

export const recordVirtualTourTimestamp = (key: VirtualTourAnalyticsTimestampKey) => {
  const session = readVirtualTourAnalyticsSession()
  if (!session || session.timestamps[key]) return

  writeVirtualTourAnalyticsSession({
    ...session,
    timestamps: { ...session.timestamps, [key]: new Date().toISOString() },
  })
}

export const recordVirtualTourStepTimestamp = (stepIndex: number) => {
  const session = readVirtualTourAnalyticsSession()
  if (!session || session.stepTimestamps[stepIndex]) return

  writeVirtualTourAnalyticsSession({
    ...session,
    stepTimestamps: { ...session.stepTimestamps, [stepIndex]: new Date().toISOString() },
  })
}

const elapsedMs = (fromIso: string | undefined, toIso?: string): number | null => {
  if (!fromIso) return null
  const from = Date.parse(fromIso)
  if (Number.isNaN(from)) return null
  const to = toIso ? Date.parse(toIso) : Date.now()
  if (Number.isNaN(to)) return null
  return Math.max(0, to - from)
}

export const getVirtualTourElapsedMetrics = (
  session: VirtualTourAnalyticsSession | null = readVirtualTourAnalyticsSession(),
  stepIndex?: number,
  nowIso = new Date().toISOString(),
): Record<string, number | null> => {
  if (!session) {
    return {
      elapsed_ms_since_start: null,
      elapsed_ms_on_step: null,
    }
  }

  const { timestamps, startedAt, stepTimestamps } = session

  return {
    elapsed_ms_since_start: elapsedMs(startedAt, nowIso),
    elapsed_ms_on_step:
      stepIndex != null ? elapsedMs(stepTimestamps[stepIndex], nowIso) : null,
    ...(timestamps.completed
      ? { elapsed_ms_total: elapsedMs(startedAt, timestamps.completed) }
      : {}),
  }
}
